import { useState, FormEvent } from 'react';

export default function FinalCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[name]');
    fields.forEach((f) => data.append(f.name, f.value));
    try {
      await fetch('https://readdy.ai/api/form/d795heprcamcd36dbc7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 overflow-hidden bg-[#111]">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://culphomeimprovement.com/wp-content/uploads/2024/10/419788193_939238311536112_4258605936431522899_n.jpg"
          alt="Culp Home Improvement project"
          className="w-full h-full object-cover object-center opacity-15"
        />
      </div>
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-4"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, #fff 0, #fff 1px, transparent 0, transparent 60px), repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 0, transparent 60px)',
        }}
      ></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Left */}
          <div>
            <p
              className="text-[#C9A87C] text-[11px] font-bold uppercase tracking-[0.22em] mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Get Started Today
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Upgrade Your Home<br />with Confidence
            </h2>
            <p
              className="text-white/60 text-lg leading-relaxed mb-10"
              style={{ fontFamily: 'Lora, serif' }}
            >
              Whether you're planning a full renovation or need reliable home improvements, Culp Home Improvement delivers results that last — on time, on budget, and built right.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-14">
              <a
                href="tel:5153215711"
                className="inline-flex items-center justify-center gap-2.5 bg-[#B8956A] hover:bg-[#9a7a52] text-white font-bold px-9 py-4 text-sm tracking-[0.12em] uppercase transition-all duration-300 whitespace-nowrap"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <i className="ri-phone-fill"></i> Call 515-321-5711
              </a>
              <button
                onClick={() => document.getElementById('estimate-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/70 text-white font-bold px-9 py-4 text-sm tracking-[0.12em] uppercase transition-all duration-300 hover:bg-white/5 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Get Free Estimate
              </button>
            </div>

            <div className="space-y-4">
              {[
                { icon: 'ri-checkbox-circle-fill', text: 'Free estimates — no obligation' },
                { icon: 'ri-shield-check-fill', text: 'Licensed, bonded & insured' },
                { icon: 'ri-user-star-fill', text: 'Owner present on every job' },
                { icon: 'ri-map-pin-2-fill', text: 'Serving Johnston, IA & surrounding areas' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <i className={`${item.icon} text-[#C9A87C] text-lg flex-shrink-0`}></i>
                  <span
                    className="text-white/65 text-sm"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div id="estimate-form" className="bg-white rounded-sm p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-[#B8956A]/10 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-fill text-[#B8956A] text-4xl"></i>
                </div>
                <h3
                  className="text-2xl font-bold text-[#1A1A1A] mb-3"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Request Received!
                </h3>
                <p
                  className="text-[#666]"
                  style={{ fontFamily: 'Lora, serif' }}
                >
                  We'll be in touch shortly to discuss your project.
                </p>
              </div>
            ) : (
              <>
                <h3
                  className="text-xl font-bold text-[#1A1A1A] mb-2"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Request a Free Estimate
                </h3>
                <p
                  className="text-[#888] text-sm mb-8"
                  style={{ fontFamily: 'Lora, serif' }}
                >
                  No obligation. We'll review your project and respond promptly.
                </p>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-[10px] font-bold text-[#555] mb-2 uppercase tracking-[0.15em]"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        Name *
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="Your full name"
                        className="w-full border border-[#E0D8D0] px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#B8956A] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-[10px] font-bold text-[#555] mb-2 uppercase tracking-[0.15em]"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        Phone *
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="Your phone number"
                        className="w-full border border-[#E0D8D0] px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#B8956A] transition-colors"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      className="block text-[10px] font-bold text-[#555] mb-2 uppercase tracking-[0.15em]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Email *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="w-full border border-[#E0D8D0] px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#B8956A] transition-colors"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-[10px] font-bold text-[#555] mb-2 uppercase tracking-[0.15em]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Project Details *
                    </label>
                    <textarea
                      name="project_details"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Describe your project — kitchen, bathroom, basement, general improvements..."
                      className="w-full border border-[#E0D8D0] px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#B8956A] transition-colors resize-none"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#B8956A] hover:bg-[#9a7a52] disabled:opacity-60 text-white font-bold py-4 text-sm tracking-[0.12em] uppercase transition-all duration-300 cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {submitting ? 'Sending...' : 'Get My Estimate'}
                  </button>
                  <div className="flex items-center justify-center gap-2 pt-1">
                    <i className="ri-shield-check-fill text-[#B8956A] text-sm"></i>
                    <p
                      className="text-center text-xs text-[#aaa]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      No spam. We'll only contact you about your project.
                    </p>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
